/**
 * 請求書管理の、請求書の発行まわりの画面の文。
 *
 * ここ1か所にまとめて、会社・プロジェクト・スタッフの3つの欄と一括の操作で
 * 言い方が割れないようにする。
 *
 * 文は Gemini が決めたもの（2026-10-09、CI-1〜CI-9・CI-22・CI-23。決め直しを含む）。言い回しを変えない。
 */
export default Object.freeze({
  /* 発行した請求書の印。入出金予測の画面と同じ言葉。 */
  issued: '発行済み',

  /* まだ発行していない請求書の印。 */
  unissued: '未発行',

  /* 請求書を発行するボタン（会社・プロジェクト・スタッフの各欄）。押すと確かめの小窓が開く。 */
  issue: '発行',

  /* 未発行の会社の請求書を、まとめて発行するボタン（会社名の見出し）。押すと確かめの小窓が開く。 */
  issueAll: '一括発行',

  /*
   * 発行する前の確かめの小窓（画面の中の小窓。ブラウザ標準の小窓ではない）。
   * 答えは下の2つのボタンで選ぶので、本文に「よろしいですか？」は書かない。
   */

  /* 小窓の見出し。1件のときも一括のときも同じ。 */
  issueDialogTitle: '請求書の発行',

  /* 小窓の本文（1件）。発行すると何が決まるか。対象の名前は、この文の上に画面が出す。 */
  issueDialogBodyOne:
    '発行すると請求番号と本日付の発行日が確定します。以後は何度開いても同じ番号・日付で表示されます。',

  /* 小窓の本文（一括）。何社ぶんかを出す。 */
  issueDialogBodyAll (count) {
    return '未発行の ' + count + ' 社の請求書を発行します。' +
      '発行すると請求番号と本日付の発行日が確定し、以後は何度開いても同じ番号・日付で表示されます。'
  },

  /* 小窓の、進めるボタン（押すと発行する）。 */
  issueDialogProceed: '発行する',

  /* 小窓の、やめるボタン（押すと何もせず閉じる）。 */
  issueDialogCancel: 'キャンセル',

  /*
   * まだ登録していない月を、登録できない人（「売上情報の管理」を持たない人。
   * 「請求書の管理」だけを持つ人）が検索したときに、新規登録の表の代わりに出す表示。
   * 上の太字（何が起きているか）と、下の1文（登録するのは誰か）。
   */
  unregisteredTitle: '該当月の請求データが登録されていません',
  unregisteredBody:
    'データの登録は「売上情報の管理」権限を持つユーザーが行う必要があります。' +
    '権限をお持ちでないため、この画面で登録することはできません。',

  /*
   * ここから下は、発行した請求書を記録として持つ変更（2026-10-11 の設計）で足した文。
   * 文は Gemini が決めたもの（2026-10-11、V1〜V24）。言い回しを変えない。
   * 文は全部 Gemini が決めたもの（V1〜V24・W1〜W3）。
   */

  /* V1 上の表（まだ請求書を発行していない明細）の見出し。 */
  unissuedHeading: '未発行の明細',

  /* V2 下の表（発行した請求書の一覧。取り消し済みも同じ一覧に出る）の見出し。 */
  issuedHeading: '発行済みの請求書',

  /* V3 下の表の列の見出し。 */
  issuedColumns: Object.freeze({
    invoiceNo: '請求番号',
    issuedOn: '発行日',
    billTo: '請求先',
    unit: '発行単位',
    amount: '請求金額',
    status: 'ステータス'
  }),

  /* V4 下の表の「発行単位」の欄。どの単位で発行した請求書か。 */
  scopeLabels: Object.freeze({
    COMPANY: '会社',
    PROJECT: 'プロジェクト',
    USER: 'スタッフ'
  }),

  /* V5 取り消した請求書の、状態の印（「発行済み」と並ぶ言葉）。 */
  voided: '取消済み',

  /* V6 下の表の、発行済みの請求書の行のボタン。押すと取り消しの小窓が開く。 */
  voidButton: '取消',

  /* V7 取り消しの小窓の見出し。 */
  voidDialogTitle: '請求書発行の取り消し',

  /* V8 取り消しの小窓の本文（相手の名前と請求番号は、この文の上に画面が出す）。 */
  voidDialogBody:
    'この操作は元に戻せません。請求番号は欠番となり、対象の明細は未発行に戻ります。' +
    '取引先への連絡が必要な場合は、各自でご対応ください。',

  /* V9 取り消しの小窓の、理由の欄の見出しと、欄の中の薄い字の例。 */
  voidReasonLabel: '取消理由（必須）',
  voidReasonPlaceholder: '誤りがあったため（例：金額間違い、あて先変更など）',

  /* V10 理由を入れずに進めたとき、欄の下に出す断り。 */
  voidReasonRequired: '取消理由を入力してください。',

  /* V11 取り消しの小窓の、進めるボタン。やめるボタンは発行の小窓と同じ「キャンセル」。 */
  voidDialogProceed: '取り消す',

  /* V12 取り消したあと、一覧の上に出す知らせ。 */
  voidDone: '請求書を取り消しました。明細を修正して再発行できます。',

  /*
   * X1（Gemini 2026-10-11） 取り消したが、同じ明細が別の発行済みの請求書にも載っているときの知らせ
   * （前の持ち方で、同じ明細を2つの単位で発行していた月）。その明細は、まだ直せない。
   * 請求番号は、複数なら「、」で並べる（2か所とも同じ並び）。
   */
  voidDoneStillOnInvoices (invoiceNos) {
    const numbers = (invoiceNos || []).join('、')
    return '請求書を取り消しました。この明細は請求書（' + numbers + '）にも含まれているため、' +
      '修正や再発行をするには請求書（' + numbers + '）の取り消しも必要です。'
  },

  /*
   * X2（Gemini 2026-10-11） 発行の小窓で、発行できたかどうかが分からないとき（通信が切れた・
   * 時間切れ・サーバの障害）。小窓の中に出す。小窓は閉じず、同じ対象のまま押し直せる。
   */
  issueResultUnknown: '発行結果を確認できませんでした。もう一度［発行する］を押してください。',

  /*
   * V15 ［発行］を押したが、その明細はほかの人がもう発行していた（発行はされず、
   * 一覧は読み直してある。下の一覧に、その請求書が出ている）。
   */
  issueNeedsReload: '対象の明細は既に発行されています。画面を更新します。',

  /* V16 更新の表で、発行済みの請求書に載っている行（入力できない行）に出す注記。 */
  lockedRow (invoiceNo) {
    return '発行済み（請求番号：' + invoiceNo + '）。修正する場合は請求書を取り消してください。'
  },

  /*
   * ここから3つは、［最新を取り込んで再作成］を「まだ発行していない分だけ作り直す」形にした変更
   * （2026-10-11）で要るようになった文。文は Gemini が決めたもの（2026-10-11、Y2〜Y4）。言い回しを変えない。
   * （前にここにあった V18「発行済みの請求書があるため再作成できません…」は、発行済みの請求書が
   * ある月でも押せるようになったので、外した。表の見出し Y1 は、ボタンと同じ「最新を取り込んで再作成」。）
   */

  /* Y2 ［最新を取り込んで再作成］を押して出る表の、見出しのすぐ下（表の上）の説明。 */
  recreateGuide:
    '未発行の明細のみ、最新の実績と金額設定で再作成した内容です。発行済みの請求書の明細は変更されません。［登録］を押すと、未発行の明細が差し替わります。',

  /*
   * Y3 ［最新を取り込んで再作成］を押したが、作り直す明細が1つも無かった（その月の明細が全部
   * 発行済みで、最新の実績・金額設定に増えた分も無い）。表は開かず、ボタンの横に出す。
   */
  recreateNothing: '再作成対象の未発行の明細がありません。発行済みの明細を修正する場合は、対象の請求書を取り消してください。',

  /*
   * Y4 再作成の表で［登録］を押したら、表を見ているあいだに、ほかの人が一部の明細を発行していた。
   * その明細は入れ直さず、残りだけ入れ替えた。「登録が完了しました」のあと、一覧の上に出す。
   * 件数は、入れ直さなかった明細の数。
   */
  recreateSkipped (count) {
    return '画面を開いている間に他のユーザーによって発行された明細が ' + count + ' 件あったため、その明細を除いて更新しました。'
  },
  /*
   * Z1 再作成の表で、保存済みの未発行の明細のうち、最新の内容に含まれないもの（当月除外など）が
   * あるとき。Y2 のすぐ下に出す（［登録］で削除されるので。監査 RC-2）。文は Gemini が決めたもの
   * （2026-10-16、紙の5回目）。言い回しを変えない。
   */
  recreateRemovedGuide:
    '以下の未発行明細は、最新の内容に含まれないため［登録］を押すと削除されます（当月除外や金額設定の変更など）。残したい明細がある場合は［キャンセル］を押してプロジェクト金額設定をご確認ください。',
  /* Z2 Z1 の下に1件ずつ並べる明細（取引先名 / プロジェクト名 / スタッフ名）。 */
  recreateRemovedLine (companyName, projectName, userName) {
    return '・' + companyName + ' / ' + projectName + ' / ' + userName
  },

  /* V19 ［更新］が済んだあとの知らせ（新規の登録の文とは別）。 */
  updated: '更新が完了しました。',

  /* V21 金額設定が未登録で登録できないとき、断りの下に出すリンク（その月のプロジェクト金額設定が開く）。 */
  openRateSetting: 'プロジェクト金額設定画面へ',

  /*
   * 場面：上の表（未発行の明細）に出す行が1つも無いとき（その月の請求する明細が、全部発行済み）。
   * 表は消さずに（［一括ダウンロード］が見出しにある）、1行でそのことを言う。
   * 伝える事実：この月に、まだ発行していない明細は無い。
   */
  /* W1（Gemini 2026-10-11） */
  noUnissued: '未発行の明細はありません。',

  /*
   * 場面：下の表の「ステータス」の欄で、取消済みの下に小さく出す1行。
   * 伝える事実：いつ・誰が・なぜ取り消したか（日付（人）と、理由）。
   */
  /* W2（Gemini 2026-10-11）：{日付} {名前}（理由：{理由}） */
  voidedDetail (voidedOn, voidedBy, reason) {
    const when = voidedOn ? String(voidedOn).replace(/-/g, '/') : ''
    const who = voidedBy ? ' ' + voidedBy : ''
    return when + who + '（理由：' + (reason || '') + '）'
  },

  /*
   * 場面：［一括ダウンロード］を押したが、その月に、有効な発行済みの請求書が1枚も無い
   * （一括ダウンロードに入るのは発行済みの請求書だけ。未発行の確認用は入らない）。
   * 伝える事実：ダウンロードできる発行済みの請求書が無い。先に［発行］する。
   */
  /* W3（Gemini 2026-10-11） */
  nothingToDownload: '発行済みの請求書がありません。請求書を発行してからダウンロードしてください。',

  /*
   * ［請求書］［一括ダウンロード］を押して、ファイルを受け取るまでのあいだのボタンの字。
   * **Gemini が決めた文ではない。**共通の保存ボタン（SaveButton）が、決まった言葉の無い操作の
   * 待ちに出している字と同じにしてある（2026-10-11）。
   */
  downloading: '処理中',

  /* V23 一覧の上に出す、［請求書］と［発行］の違いと、直したいときの説明。 */
  guide:
    '［請求書］で表示される PDF は発行前の確認用となり、請求番号と発行日は記載されません。' +
    '取引先へ送付する前に［発行］を押して発行してください。発行すると請求番号と発行日が確定します。' +
    '内容を修正したい場合は、下の一覧から一度請求書を取り消してください。'
})
